import type { ClaimGroupValueRollup } from "@hp/types";
import { formatINR } from "../../lib/workbenchFormat";

interface ClaimGroupValueDisplayProps {
  rollup: Pick<ClaimGroupValueRollup, "principal_high" | "carrying_cost_high" | "total_high" | "member_count">;
  compact?: boolean;
}

/** Principal + LPS + total for a grouped claim row (summed across FY tranches). */
export function ClaimGroupValueDisplay({ rollup, compact = false }: ClaimGroupValueDisplayProps) {
  const hasCarrying = rollup.carrying_cost_high > 0;

  // No quantified value (e.g. a claim rejected before any amount was worked out): show nothing rather than ₹0.
  if (rollup.total_high <= 0 && rollup.principal_high <= 0 && !hasCarrying) return null;

  if (compact) {
    return (
      <div className="space-y-0.5 text-xs">
        <div className="font-mono tabular-nums text-slate-900">
          Principal {formatINR(rollup.principal_high)}
        </div>
        {hasCarrying ? (
          <div className="font-mono tabular-nums text-slate-600">LPS {formatINR(rollup.carrying_cost_high)}</div>
        ) : null}
        <div className="font-mono tabular-nums font-medium text-slate-900">Total {formatINR(rollup.total_high)}</div>
        {rollup.member_count > 1 ? (
          <div className="text-slate-500">{rollup.member_count} FY tranches</div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="font-mono text-sm font-semibold tabular-nums text-slate-900">
        {formatINR(rollup.total_high)}
      </div>
      <div className="text-xs text-slate-500">
        Principal {formatINR(rollup.principal_high)}
        {hasCarrying ? ` · LPS ${formatINR(rollup.carrying_cost_high)}` : ""}
      </div>
      {rollup.member_count > 1 ? (
        <div className="text-[0.6875rem] text-slate-400">Σ {rollup.member_count} financial years</div>
      ) : null}
    </div>
  );
}
